import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Loader2, User, Building2, DollarSign, ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { AssignmentPaymentService, type PaymentSource } from "@/services/assignment-payment.service";
import { currentAssigneeOf, currentPayOf, mayGiveWorker, typedPay, type AssigningHat, type ResourceAssignment } from "@/lib/appointment-assignment";
import type { appointment_status } from "@/models/appointment.model";
import { typedMoneyProblem } from "@/lib/typed-money";

interface AssignableResource {
  id: string;
  name: string;
  type: "individual" | "sub";
}

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onAssign: (assignment: ResourceAssignment) => Promise<void>;
  isLoading?: boolean;
  isEditMode?: boolean;
  hat?: AssigningHat;
}

export default function AssignmentModal({
  isOpen,
  onClose,
  appointment,
  onAssign,
  isLoading = false,
  isEditMode = false,
  hat = "vendor",
}: AssignmentModalProps) {
  const [selectedResource, setSelectedResource] = useState<string>("");
  const currentWorker = appointment?.assigned_user?.id ?? null;
  const onlyCurrentWorker = hat === "subcontractor" && currentWorker !== null && !mayGiveWorker(appointment.status as appointment_status, true);
  const [comboOpen, setComboOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (comboOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [comboOpen]);
  const [payText, setPayText] = useState("");
  const [suggestedPay, setSuggestedPay] = useState<number>(0);
  const [resources, setResources] = useState<{
    individuals: AssignableResource[];
    subs: AssignableResource[];
  }>({ individuals: [], subs: [] });
  const [loadingResources, setLoadingResources] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentSource, setPaymentSource] = useState<PaymentSource>("fallback");
  const [suggestionFailed, setSuggestionFailed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadResources();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && (isEditMode || onlyCurrentWorker)) {
      if (resources.individuals.length === 0 && resources.subs.length === 0) {
        return;
      }

      const listed = [...resources.individuals, ...resources.subs].map((resource) => resource.id);
      const current = currentAssigneeOf(hat, appointment, listed);
      if (current) setSelectedResource(current);
    } else if (!isOpen) {
      setSelectedResource("");
      setPayText("");
    }
  }, [isOpen, isEditMode, onlyCurrentWorker, appointment, resources, hat]);

  useEffect(() => {
    if (isOpen && appointment && hat === "vendor") {
      calculatePayment();
    }
  }, [isOpen, appointment, hat]);

  const currentPay = currentPayOf(selectedResource, appointment);

  useEffect(() => {
    setPayText(currentPay === null ? "" : String(currentPay));
  }, [selectedResource]);

  const loadResources = async () => {
    setLoadingResources(true);
    try {
      setResources(
        hat === "vendor"
          ? await IndividualsRepository.listAssignableResources()
          : { individuals: (await IndividualsRepository.listForAssignment()).filter((individual) => !onlyCurrentWorker || individual.id === currentWorker), subs: [] }
      );
    } catch (error) {
      console.error("Failed to load assignable resources:", error);
    } finally {
      setLoadingResources(false);
    }
  };

  const calculatePayment = async () => {
    if (!appointment?.service?.id) {
      setSuggestedPay(0);
      setPaymentSource("fallback");
      return;
    }

    if (!appointment?.vendor?.id || !appointment?.property?.id) {
      setSuggestedPay(0);
      setPaymentSource("fallback");
      return;
    }

    setLoadingPayment(true);
    setSuggestionFailed(false);
    try {
      const result = await AssignmentPaymentService.calculatePayment(appointment.vendor.id, appointment.property.id, appointment.service.id);
      setSuggestedPay(result.payment);
      setPaymentSource(result.source);
    } catch (error) {
      console.error("Failed to calculate payment:", error);
      setSuggestionFailed(true);
    } finally {
      setLoadingPayment(false);
    }
  };

  const pay = typedPay(payText);
  const payProblem = typedMoneyProblem(payText);

  const handleAssign = async () => {
    if (!selectedResource || pay === null) {
      return;
    }

    const selectedResourceData = [...resources.individuals, ...resources.subs].find((r) => r.id === selectedResource);

    if (!selectedResourceData) {
      return;
    }

    try {
      await onAssign({ type: selectedResourceData.type, resourceId: selectedResource, pay });
      handleClose();
    } catch (error) {
      console.error("Assignment failed:", error);
    }
  };

  const handleClose = () => {
    setSelectedResource("");
    setPayText("");
    onClose();
  };

  const getPaymentSourceLabel = () => {
    switch (paymentSource) {
      case "custom_pricing":
        return "Custom pricing";
      case "default_service":
        return "Default service price";
      case "fallback":
        return "No pricing found";
      default:
        return "Unknown";
    }
  };

  const isFormValid = Boolean(selectedResource) && pay !== null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Change Resource and Payment" : "Assign Resource"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the assigned resource and payment amount for this appointment."
              : "Assign a sub contractor or individual to this appointment and set the payment amount."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Resource Selection */}
          <div className="space-y-2">
            <Label htmlFor="resource">Assign Resource</Label>
            {loadingResources ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="ml-2">Loading resources...</span>
              </div>
            ) : (
              <div className="relative">
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={comboOpen}
                  onClick={() => setComboOpen((o) => !o)}
                  className={cn(
                    "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs ring-offset-background",
                    "focus:outline-none focus:ring-1 focus:ring-ring"
                  )}
                >
                  {selectedResource ? (
                    <span className="flex items-center gap-2">
                      {[...resources.subs, ...resources.individuals].find((r) => r.id === selectedResource)?.type === "sub" ? (
                        <Building2 className="w-4 h-4 shrink-0" />
                      ) : (
                        <User className="w-4 h-4 shrink-0" />
                      )}
                      {[...resources.subs, ...resources.individuals].find((r) => r.id === selectedResource)?.name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Select individual or sub contractor</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </button>

                {comboOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-10 rounded-md border bg-popover shadow-md overflow-hidden">
                    <Command>
                      <CommandInput ref={searchInputRef} placeholder="Search..." />
                      <CommandList className="max-h-52">
                        <CommandEmpty>No results found.</CommandEmpty>

                        {resources.subs.length > 0 && (
                          <CommandGroup heading="Sub Contractors">
                            {resources.subs.map((sub) => (
                              <CommandItem
                                key={sub.id}
                                value={sub.name}
                                onSelect={() => {
                                  setSelectedResource(sub.id);
                                  setComboOpen(false);
                                }}
                              >
                                <Building2 className="w-4 h-4 mr-2 shrink-0" />
                                {sub.name}
                                <Check
                                  className={cn("ml-auto h-4 w-4", selectedResource === sub.id ? "opacity-100" : "opacity-0")}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}

                        {resources.subs.length > 0 && resources.individuals.length > 0 && <CommandSeparator />}

                        {resources.individuals.length > 0 && (
                          <CommandGroup heading="Individual / Individuals">
                            {resources.individuals.map((individual) => (
                              <CommandItem
                                key={individual.id}
                                value={individual.name}
                                onSelect={() => {
                                  setSelectedResource(individual.id);
                                  setComboOpen(false);
                                }}
                              >
                                <User className="w-4 h-4 mr-2 shrink-0" />
                                {individual.name}
                                <Check
                                  className={cn("ml-auto h-4 w-4", selectedResource === individual.id ? "opacity-100" : "opacity-0")}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}

                        {resources.subs.length === 0 && resources.individuals.length === 0 && (
                          <div className="px-2 py-6 text-center text-sm text-muted-foreground">No resources available</div>
                        )}
                      </CommandList>
                    </Command>
                  </div>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Payment Input */}
          <div className="space-y-2">
            <Label htmlFor="payment">Payment</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="payment"
                type="number"
                min="0"
                step="0.01"
                value={payText}
                onChange={(e) => setPayText(e.target.value)}
                className="pl-10"
                placeholder={hat !== "vendor" ? "0.00" : suggestionFailed ? "" : suggestedPay.toFixed(2)}
              />
              {loadingPayment && (
                <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {currentPay !== null
                ? "Leave blank to keep the current pay"
                : hat !== "vendor"
                  ? "Leave blank to pay nothing"
                  : suggestionFailed
                    ? "The suggested pay could not be loaded. Type the pay, or leave blank to pay the service's price list."
                    : `Leave blank to pay $${suggestedPay.toFixed(2)} (${getPaymentSourceLabel()})`}
            </p>
            {payProblem && <p className="text-sm text-destructive">{payProblem}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {isEditMode ? "Updating..." : "Assigning..."}
              </>
            ) : isEditMode ? (
              "Update Resource and Payment"
            ) : (
              "Assign Resource"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
